import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productService } from '../services/api';
import { useCategories } from '../hooks/useCategories';
import { getImageUrl } from '../utils/imageUtils';
import { useAuth } from '../hooks/useAuth';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import DashboardSidebar from '../components/DashboardSidebar';

// ── Toast ────────────────────────────────────────────────────────────────────

function Toast({ message, type, onClose }) {
  if (!message) return null;
  return (
    <div className="fixed dashboard-floating-bottom left-1/2 -translate-x-1/2 z-[100] animate-fade-in">
      <div className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg text-sm font-bold max-w-md ${
        type === 'error' ? 'bg-error text-on-error'
        : type === 'warning' ? 'bg-secondary text-on-secondary'
        : type === 'info' ? 'bg-secondary-container text-on-secondary-container'
        : 'bg-primary text-on-primary'
      }`}>
        <span className="material-symbols-outlined text-base">
          {type === 'error' ? 'error' : type === 'warning' ? 'warning' : type === 'info' ? 'info' : 'check_circle'}
        </span>
        <span className="flex-1">{message}</span>
        <a onClick={onClose} className="ml-2 cursor-pointer opacity-70 hover:opacity-100 shrink-0">
          <span className="material-symbols-outlined text-base">close</span>
        </a>
      </div>
    </div>
  );
}

// ── Conditions ───────────────────────────────────────────────────────────────

const CONDITIONS = [
  { value: 'NEW', labelKey: 'Nuevo' },
  { value: 'LIKE_NEW', labelKey: 'Como Nuevo' },
  { value: 'USED', labelKey: 'Usado' },
];

// ── Main ─────────────────────────────────────────────────────────────────────

function EditProductNew() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const theme = getThemeVars(ACTIVE_THEME);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'success' });
  const [unauthorized, setUnauthorized] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    condition: '',
    categoryId: null,
    stock: '',
    unlimitedStock: false,
  });

  // Images state
  // Category dropdown (custom — avoids iOS/Android native picker breaking fixed-height shell)
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryRef = useRef(null);
  useEffect(() => {
    if (!categoryOpen) return;
    const onDocMouseDown = (e) => { if (categoryRef.current && !categoryRef.current.contains(e.target)) setCategoryOpen(false); };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('touchstart', onDocMouseDown);
    return () => { document.removeEventListener('mousedown', onDocMouseDown); document.removeEventListener('touchstart', onDocMouseDown); };
  }, [categoryOpen]);

  const [primaryImageId, setPrimaryImageId] = useState(null);
  const [newImages, setNewImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  // Original data to check for changes
  const [originalData, setOriginalData] = useState(null);

  // Validation errors
  const [errors, setErrors] = useState({});
  const { categories } = useCategories();

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: '', type: 'success' }), 5000);
  };

  // ── Load data ─────────────────────────────────────────────────────────────

  useEffect(() => {
    loadProduct();
  }, [id]);

  // (Categorías cacheadas via useCategories — no requiere fetch local)

  const loadProduct = async () => {
    setLoading(true);
    try {
      const productResponse = await productService.getProductById(id);
      const product = productResponse.data;

      if (product.publisherId !== user?.id) {
        setUnauthorized(true);
        setLoading(false);
        return;
      }

      const imagesResponse = await productService.getProductImages(id);
      const images = imagesResponse.data || [];

      const productData = {
        name: product.name || '',
        description: product.description || '',
        price: product.price || '',
        condition: product.condition || 'USED',
        categoryId: product.categoryId || null,
        stock: product.stock == null ? '' : String(product.stock),
        unlimitedStock: product.stock == null,
      };

      setFormData(productData);
      setOriginalData(productData);
      setPrimaryImageId(product.imageId);

      const existingPreviews = images.map(img => ({
        id: img.imageId || img.id,
        url: getImageUrl(img.imageId || img.id),
        isPrimary: (img.imageId || img.id) === product.imageId,
        isExisting: true,
      }));
      setImagePreviews(existingPreviews);
    } catch (err) {
      showToast(t('Error al cargar el producto'), 'error');
      setTimeout(() => navigate('/profile?tab=products'), 2000);
    } finally {
      setLoading(false);
    }
  };

  // ── Input handlers ────────────────────────────────────────────────────────

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'price' && value !== '' && !isNaN(parseFloat(value)) && parseFloat(value) > 999999999) {
      setErrors(prev => ({ ...prev, price: t('El precio no puede superar 999.999.999') }));
    } else if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleStockChange = (event) => {
    const v = event.target.value.replace(/[^0-9]/g, '');
    setFormData(prev => ({ ...prev, stock: v }));
    if (v && parseInt(v, 10) > 1000000) {
      setErrors(prev => ({ ...prev, stock: t('El stock no puede superar 1.000.000') }));
    } else if (errors.stock) {
      setErrors(prev => ({ ...prev, stock: '' }));
    }
  };

  const toggleUnlimitedStock = () => {
    setFormData(prev => ({ ...prev, unlimitedStock: !prev.unlimitedStock }));
    if (errors.stock) setErrors(prev => ({ ...prev, stock: '' }));
  };

  // ── Image handlers ────────────────────────────────────────────────────────

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);

    const totalImages = imagePreviews.length + files.length;
    if (totalImages > 5) {
      showToast(t('Maximo 5 imagenes por producto'), 'error');
      return;
    }

    const validFiles = [];
    files.forEach(file => {
      if (!file.type.startsWith('image/')) {
        showToast(t('{{fileName}} no es una imagen valida', { fileName: file.name }), 'error');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showToast(t('{{fileName}} supera el tamano maximo de 5MB', { fileName: file.name }), 'error');
        return;
      }

      validFiles.push(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        const preview = {
          id: `new-${Date.now()}-${Math.random()}`,
          url: reader.result,
          file: file,
          isPrimary: false,
          isExisting: false,
        };
        setImagePreviews(prev => [...prev, preview]);
      };
      reader.readAsDataURL(file);
    });

    setNewImages(prev => [...prev, ...validFiles]);
  };

  const removeImage = async (imageToRemove) => {
    if (imageToRemove.isExisting) {
      try {
        await productService.deleteProductImage(id, imageToRemove.id);
        if (imageToRemove.id === primaryImageId) {
          setPrimaryImageId(null);
        }
        showToast(t('Imagen eliminada'));
      } catch (err) {
        showToast(t('Error al eliminar la imagen'), 'error');
        return;
      }
    } else {
      setNewImages(prev => prev.filter(img => img !== imageToRemove.file));
    }
    setImagePreviews(prev => prev.filter(img => img.id !== imageToRemove.id));
  };

  const setPrimaryImage = async (image) => {
    if (!image.isExisting) {
      showToast(t('Primero debes guardar los cambios para establecer esta imagen como principal'), 'info');
      return;
    }

    try {
      await productService.setPrimaryImage(id, image.id);
      setPrimaryImageId(image.id);
      setImagePreviews(prev => prev.map(img => ({
        ...img,
        isPrimary: img.id === image.id,
      })));
      showToast(t('Imagen principal actualizada'));
    } catch (err) {
      showToast(t('Error al establecer imagen principal'), 'error');
    }
  };

  const uploadNewImages = async () => {
    if (newImages.length === 0) return;

    const uploadedCount = { success: 0, failed: 0 };

    for (const image of newImages) {
      try {
        await productService.addProductImage(id, image);
        uploadedCount.success++;
      } catch (err) {
        uploadedCount.failed++;
      }
    }

    if (uploadedCount.failed > 0) {
      showToast(
        t('{{success}} imagenes subidas, {{failed}} fallaron', {
          success: uploadedCount.success,
          failed: uploadedCount.failed,
        }),
        'warning'
      );
    } else if (uploadedCount.success > 0) {
      showToast(
        t('{{count}} imagenes subidas exitosamente', { count: uploadedCount.success })
      );
    }

    setNewImages([]);
    await loadProduct();
  };

  // ── Validation ────────────────────────────────────────────────────────────

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('El nombre es requerido');
    } else if (formData.name.length < 3) {
      newErrors.name = t('El nombre debe tener al menos 3 caracteres');
    }

    if (!formData.description.trim()) {
      newErrors.description = t('La descripcion es requerida');
    } else if (formData.description.length < 20) {
      newErrors.description = t('La descripcion debe tener al menos 20 caracteres');
    }

    if (!formData.price || formData.price <= 0) {
      newErrors.price = t('El precio debe ser mayor a 0');
    } else if (parseFloat(formData.price) > 999999999) {
      newErrors.price = t('El precio no puede superar 999.999.999');
    }

    if (!formData.unlimitedStock) {
      const stockNum = parseInt(formData.stock, 10);
      if (!formData.stock || isNaN(stockNum) || stockNum < 1) {
        newErrors.stock = t('El stock debe ser mayor o igual a 1');
      } else if (stockNum > 1000000) {
        newErrors.stock = t('El stock no puede superar 1.000.000');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);

    try {
      if (newImages.length > 0) {
        await uploadNewImages();
      }

      const updateData = {
        name: formData.name,
        description: formData.description,
        price: parseInt(formData.price),
        condition: formData.condition,
        categoryId: formData.categoryId,
        unlimitedStock: formData.unlimitedStock,
        stock: formData.unlimitedStock ? null : parseInt(formData.stock, 10),
      };

      await productService.updateProduct(id, updateData);

      showToast(t('Producto actualizado exitosamente'));

      setTimeout(() => {
        navigate(`/products/${id}`);
      }, 1500);
    } catch (err) {
      showToast(err.response?.data?.message || t('Error al actualizar el producto'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const hasChanges = () => {
    if (!originalData) return false;
    return Object.keys(formData).some(key => formData[key] !== originalData[key]) || newImages.length > 0;
  };

  const Shell = ({ children }) => (
    <div
      className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col tablet-sm:flex-row"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <DashboardSidebar activePath="/my-products" />
      <main className="flex-1 h-[100dvh] overflow-y-auto hide-scrollbar cm-pb-mobile-nav">
        <div className="px-6 tablet-sm:px-12 pt-8 pb-20 max-w-6xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );

  // ── Loading state ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Shell contentClassName="max-w-6xl min-h-[50dvh] flex items-center justify-center">
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
              <p className="text-on-surface-variant text-sm">{t('Cargando producto...')}</p>
            </div>
          </div>
      </Shell>
    );
  }

  // ── Unauthorized state ────────────────────────────────────────────────────

  if (unauthorized) {
    return (
      <Shell contentClassName="max-w-6xl min-h-[50dvh] flex items-center justify-center">
          <div className="flex items-center justify-center h-full">
            <div className="bg-surface border border-outline rounded-xl shadow-sm p-8 max-w-md text-center">
              <span className="material-symbols-outlined text-5xl text-error mb-4">block</span>
              <h2 className="text-xl font-bold text-on-surface mb-2">
                {t('No tienes permiso para editar este producto')}
              </h2>
              <button
                onClick={() => navigate('/profile?tab=products')}
                className="mt-4 px-6 py-2.5 rounded-lg bg-primary text-on-primary font-medium hover:opacity-90 transition-opacity inline-flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-lg">arrow_back</span>
                {t('Volver a mis productos')}
              </button>
            </div>
          </div>
      </Shell>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <Shell>

          {/* ── Header ── */}
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-on-surface/5 transition-colors"
            >
              <span className="material-symbols-outlined text-on-surface">arrow_back</span>
            </button>
            <h1 className="text-2xl font-bold text-on-surface">{t('Editar Producto')}</h1>
          </div>

          {/* ── Two-column layout ── */}
          <div className="grid grid-cols-1 tablet-sm:grid-cols-2 gap-6">

            {/* ═══ Left Column: Product Info ═══ */}
            <div className="bg-surface border border-outline rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-on-surface mb-5 flex items-center gap-2">
                <span className="material-symbols-outlined text-xl text-primary">info</span>
                {t('Informacion del Producto')}
              </h2>

              <div className="space-y-5">
                {/* Product Name */}
                <div className="relative">
                  <input
                    type="text"
                    id="ep-name"
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    className={`peer w-full border-2 rounded-lg px-3.5 pt-5 pb-2 text-base text-on-surface bg-white placeholder-transparent transition-colors focus:outline-none ${
                      errors.name ? 'border-error' : 'border-outline-variant/30 focus:border-primary'
                    }`}
                    placeholder={t('Nombre del producto')}
                  />
                  <label
                    htmlFor="ep-name"
                    className={`absolute left-3 -top-2.5 bg-white px-1 text-sm transition-colors ${
                      errors.name ? 'text-error' : 'text-on-surface-variant peer-focus:text-primary'
                    }`}
                  >
                    {t('Nombre del producto')} *
                  </label>
                  {errors.name && <p className="text-error text-xs mt-1">{errors.name}</p>}
                </div>

                {/* Description */}
                <div className="relative">
                  <textarea
                    id="ep-description"
                    rows={4}
                    value={formData.description}
                    onChange={handleInputChange('description')}
                    className={`peer w-full border-2 rounded-lg px-3.5 pt-5 pb-2 text-base text-on-surface bg-white placeholder-transparent transition-colors resize-none focus:outline-none ${
                      errors.description ? 'border-error' : 'border-outline-variant/30 focus:border-primary'
                    }`}
                    placeholder={t('Descripcion')}
                  />
                  <label
                    htmlFor="ep-description"
                    className={`absolute left-3 -top-2.5 bg-white px-1 text-sm transition-colors ${
                      errors.description ? 'text-error' : 'text-on-surface-variant peer-focus:text-primary'
                    }`}
                  >
                    {t('Descripcion')} *
                  </label>
                  {errors.description && <p className="text-error text-xs mt-1">{errors.description}</p>}
                </div>

                {/* Price + Condition row */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Price */}
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-variant/60 text-base z-10 pointer-events-none">$</span>
                    <input
                      type="number"
                      id="ep-price"
                      min="1"
                      max="999999999"
                      value={formData.price}
                      onChange={handleInputChange('price')}
                      className={`peer w-full border-2 rounded-lg pl-8 pr-3.5 pt-5 pb-2 text-base text-on-surface bg-white placeholder-transparent transition-colors focus:outline-none ${
                        errors.price ? 'border-error' : 'border-outline-variant/30 focus:border-primary'
                      }`}
                      placeholder={t('Precio')}
                    />
                    <label
                      htmlFor="ep-price"
                      className={`absolute left-3 -top-2.5 bg-white px-1 text-sm transition-colors ${
                        errors.price ? 'text-error' : 'text-on-surface-variant peer-focus:text-primary'
                      }`}
                    >
                      {t('Precio')} *
                    </label>
                    {errors.price && <p className="text-error text-xs mt-1">{errors.price}</p>}
                  </div>

                  {/* Condition */}
                  <div className="relative">
                    <select
                      id="ep-condition"
                      value={formData.condition}
                      onChange={handleInputChange('condition')}
                      className="peer w-full border-2 border-outline-variant/30 rounded-lg px-3.5 pt-5 pb-2 text-base text-on-surface bg-white appearance-none cursor-pointer transition-colors focus:outline-none focus:border-primary"
                    >
                      {CONDITIONS.map(c => (
                        <option key={c.value} value={c.value}>{t(c.labelKey)}</option>
                      ))}
                    </select>
                    <label
                      htmlFor="ep-condition"
                      className="absolute left-3 -top-2.5 bg-white px-1 text-sm text-on-surface-variant peer-focus:text-primary transition-colors"
                    >
                      {t('Condicion')}
                    </label>
                    <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant/50 text-xl pointer-events-none">
                      arrow_drop_down
                    </span>
                  </div>
                </div>

                {/* Category — custom dropdown (native select breaks the fixed-height shell on mobile) */}
                <div className="relative" ref={categoryRef}>
                  <button
                    type="button"
                    id="ep-category"
                    aria-haspopup="listbox"
                    aria-expanded={categoryOpen}
                    onClick={() => setCategoryOpen(o => !o)}
                    className={`peer w-full border-2 border-outline-variant/30 rounded-lg px-3.5 pt-5 pb-2 min-h-[52px] text-base text-left bg-white cursor-pointer transition-colors focus:outline-none focus:border-primary flex items-center justify-between ${!formData.categoryId ? 'text-on-surface-variant/50' : 'text-on-surface'}`}
                  >
                    <span className="truncate">
                      {formData.categoryId
                        ? (categories.find(c => String(c.id) === String(formData.categoryId))?.name || t('Sin categoria'))
                        : t('Sin categoria')}
                    </span>
                    <span className="material-symbols-outlined text-on-surface-variant/50 text-xl shrink-0 ml-2">arrow_drop_down</span>
                  </button>
                  <label
                    htmlFor="ep-category"
                    className="absolute left-3 -top-2.5 bg-white px-1 text-sm text-on-surface-variant peer-focus:text-primary transition-colors pointer-events-none"
                  >
                    {t('Categoria')}
                  </label>
                  {categoryOpen && (
                    <div role="listbox" className="absolute top-full left-0 right-0 mt-1 bg-surface-container rounded-xl shadow-xl border border-outline-variant/20 p-1.5 z-50 max-h-[280px] overflow-y-auto">
                      <button
                        type="button"
                        role="option"
                        aria-selected={!formData.categoryId}
                        onClick={() => { setFormData(p => ({ ...p, categoryId: null })); setCategoryOpen(false); }}
                        className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-colors text-on-surface ${!formData.categoryId ? 'bg-primary-container/30 font-bold' : 'font-medium hover:bg-surface-container-high'}`}
                      >
                        {t('Sin categoria')}
                      </button>
                      {categories.map(c => {
                        const sel = String(c.id) === String(formData.categoryId);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            role="option"
                            aria-selected={sel}
                            onClick={() => { setFormData(p => ({ ...p, categoryId: c.id })); setCategoryOpen(false); }}
                            className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-colors text-on-surface ${sel ? 'bg-primary-container/30 font-bold' : 'font-medium hover:bg-surface-container-high'}`}
                          >
                            {c.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Stock */}
                <div>
                  <label className="block text-sm font-medium text-on-surface-variant mb-2">
                    {t('Disponibilidad')}
                  </label>
                  <div className="flex flex-col mobile-sm:flex-row gap-3 items-stretch mobile-sm:items-start">
                    {/* Quantity input */}
                    <div className={`flex-1 transition-opacity ${formData.unlimitedStock ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
                      <div className="relative">
                        <input
                          type="text"
                          inputMode="numeric"
                          id="ep-stock"
                          value={formData.unlimitedStock ? '' : formData.stock}
                          onChange={handleStockChange}
                          disabled={formData.unlimitedStock}
                          className={`peer w-full border-2 rounded-lg px-3.5 pt-5 pb-2 text-base text-on-surface bg-white placeholder-transparent transition-colors focus:outline-none ${
                            errors.stock ? 'border-error' : 'border-outline-variant/30 focus:border-primary'
                          }`}
                          placeholder={t('Stock')}
                        />
                        <label
                          htmlFor="ep-stock"
                          className={`absolute left-3 -top-2.5 bg-white px-1 text-sm transition-colors ${
                            errors.stock ? 'text-error' : 'text-on-surface-variant peer-focus:text-primary'
                          }`}
                        >
                          {t('Cantidad disponible')} *
                        </label>
                      </div>
                      {errors.stock && !formData.unlimitedStock && (
                        <p className="text-error text-xs mt-1">{errors.stock}</p>
                      )}
                    </div>

                    {/* Unlimited toggle pill */}
                    <a
                      onClick={toggleUnlimitedStock}
                      role="button"
                      aria-pressed={formData.unlimitedStock}
                      className={`flex items-center justify-center gap-2 px-4 mobile-sm:px-5 py-3 rounded-lg border-2 cursor-pointer transition-all select-none whitespace-nowrap ${
                        formData.unlimitedStock
                          ? 'border-primary bg-primary text-on-primary shadow-sm'
                          : 'border-outline-variant/30 bg-white text-on-surface-variant hover:border-primary/40 hover:text-on-surface'
                      }`}
                    >
                      <span className="material-symbols-outlined text-[20px]">all_inclusive</span>
                      <span className="text-sm font-semibold">{t('Stock ilimitado')}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* ═══ Right Column: Images ═══ */}
            <div className="bg-surface border border-outline rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-semibold text-on-surface flex items-center gap-2">
                  <span className="material-symbols-outlined text-xl text-primary">image</span>
                  {t('Imagenes del Producto')}
                  <span className="text-sm font-normal text-on-surface-variant ml-1">
                    ({imagePreviews.length}/5)
                  </span>
                </h2>
                {imagePreviews.length < 5 && (
                  <label className="px-4 py-2 rounded-lg border-2 border-primary text-primary font-medium text-sm cursor-pointer hover:bg-primary/5 transition-colors inline-flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-lg">add_photo_alternate</span>
                    {t('Agregar')}
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </label>
                )}
              </div>

              {imagePreviews.length === 0 ? (
                /* Empty state */
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-outline-variant/40 rounded-xl p-10 cursor-pointer hover:border-primary/50 hover:bg-primary/[0.02] transition-colors">
                  <span className="material-symbols-outlined text-5xl text-on-surface-variant/40 mb-3">cloud_upload</span>
                  <p className="text-on-surface-variant text-sm text-center">
                    {t('No hay imagenes. Haz clic para subir imagenes.')}
                  </p>
                  <p className="text-on-surface-variant/50 text-xs mt-1">{t('Maximo 5 imagenes, 5MB cada una')}</p>
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </label>
              ) : (
                /* Image grid */
                <div className="grid grid-cols-2 gap-3">
                  {imagePreviews.map((preview, index) => (
                    <div
                      key={preview.id}
                      className={`relative group rounded-xl overflow-hidden border-2 transition-colors ${
                        preview.isPrimary ? 'border-primary' : 'border-outline-variant/20'
                      }`}
                    >
                      {/* Image */}
                      <img
                        src={preview.url}
                        alt={t('Imagen {{index}}', { index: index + 1 })}
                        className="w-full h-36 object-cover"
                      />

                      {/* Overlay buttons (visible on hover) */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-start justify-end p-1.5 gap-1 opacity-0 group-hover:opacity-100">
                        {/* Set primary */}
                        <button
                          onClick={() => !preview.isPrimary && setPrimaryImage(preview)}
                          disabled={!preview.isExisting}
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                            preview.isPrimary
                              ? 'bg-primary text-on-primary'
                              : 'bg-white/80 text-on-surface hover:bg-white disabled:opacity-40'
                          }`}
                          title={preview.isPrimary ? t('Es la imagen principal') : t('Establecer como principal')}
                        >
                          <span className="material-symbols-outlined text-lg">
                            {preview.isPrimary ? 'star' : 'star_outline'}
                          </span>
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => removeImage(preview)}
                          className="w-8 h-8 rounded-full flex items-center justify-center bg-white/80 text-error hover:bg-white transition-colors"
                          title={t('Eliminar imagen')}
                        >
                          <span className="material-symbols-outlined text-lg">delete</span>
                        </button>
                      </div>

                      {/* Primary badge */}
                      {preview.isPrimary && (
                        <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-primary text-on-primary text-xs font-bold">
                          {t('Principal')}
                        </div>
                      )}

                      {/* New badge */}
                      {!preview.isExisting && (
                        <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-tertiary text-on-tertiary text-xs font-bold">
                          {t('Nueva')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Pending new images info */}
              {newImages.length > 0 && (
                <div className="mt-4 flex items-center gap-2 px-3 py-2.5 rounded-lg bg-secondary-container/40 text-on-secondary-container text-sm">
                  <span className="material-symbols-outlined text-lg">info</span>
                  {t('{{count}} imagen(es) nueva(s) pendiente(s) de guardar', { count: newImages.length })}
                </div>
              )}
            </div>
          </div>

          {/* ── Action Buttons Footer ── */}
          <div className="flex flex-col-reverse mobile-sm:flex-row mobile-sm:items-center mobile-sm:justify-between gap-3 mt-6 bg-surface border border-outline rounded-xl shadow-sm px-6 py-4">
            <button
              onClick={() => navigate(`/products/${id}`)}
              disabled={submitting}
              className="w-full mobile-sm:w-auto px-6 py-2.5 rounded-lg border-2 border-primary text-primary font-medium hover:bg-primary/5 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              <span className="material-symbols-outlined text-lg">close</span>
              {t('Cancelar')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !hasChanges()}
              className="w-full mobile-sm:w-auto px-6 py-2.5 rounded-lg bg-primary text-on-primary font-medium hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              <span className="material-symbols-outlined text-lg">
                {submitting ? 'hourglass_empty' : 'save'}
              </span>
              {submitting ? t('Guardando...') : t('Guardar Cambios')}
            </button>
          </div>

      {/* Toast notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: '', type: 'success' })}
      />
    </Shell>
  );
}

export default EditProductNew;
